<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { type RoutePointAlertsType } from './AssignmentApi';
    import {
        VehicleLimitItem,
        type VehicleLimitExceededType,
    } from './VehicleLimit/VehicleLimitItem';
    import TemplateIcon from '../../shared/Icons/TemplateIcon.svelte';
    import Duration from '../../shared/Duration.svelte';
    import Time from '../../shared/Time.svelte';
    import Opacity from '../../shared/Opacity.svelte';

    export let alerts: VehicleLimitExceededType[];
    export let title: string | null = null;
    export let values: boolean = false;
    export let units: boolean = false;
    export let percent: boolean = false;
    export let scale: number = 0.5;
    export let hasNonNormativeOrders: boolean = false;
    export let doubtReasons: Record<string, string>;

    let types: Record<string, { title: string; unit: string }> = {
        parcel_limit: { title: $_('Vehicle_parcel_limit'), unit: '' },
        weight_limit: { title: $_('Vehicle_total_weight'), unit: 'kg' },
    };

    if (title === null && alerts.length == 1) {
        title = $_(alerts[0].alt.match(/unset$/) ? alerts[0].alt : alerts[0].alt + '_wo_values');
    }
</script>

<div class="container">
    {#if title}
        <div
            class="title"
            style="color: #000000; {alerts.length === 0 ? ' border-bottom: 0;' : ''}"
        >
            {title}
        </div>
    {/if}
    {#each alerts as alert}
        {@const valueToUse =
            alert.maxValue && alert.value <= alert.limit ? alert.maxValue : alert.value}
        <div class="alert">
            <TemplateIcon
                src={'data:image/svg+xml;base64,' +
                    btoa(alert.src.replace(/\{color\}/g, alert.color))}
                {scale}
                alt={alert.title}
            />
            {#if values}
                {#if alert.color !== VehicleLimitItem._VEHICLE_LIMIT_COLOR_UNSET}
                    {#if alert.type === 'work_time'}
                        <div
                            class="values"
                            style="display: flex; justify-items: center; flex-direction: row;"
                        >
                            <div class="value"><Duration seconds={valueToUse} /></div>
                            /
                            <div class="limit"><Duration seconds={alert.limit} /></div>
                        </div>
                    {:else if alert.type === 'work_time_start'}
                        <div
                            class="values"
                            style="display: flex; justify-items: center; flex-direction: row;column-gap: 3px;"
                        >
                            <div class="value">
                                <Time
                                    seconds={valueToUse}
                                    displayFormat="time"
                                    reduceTo24h={true}
                                />
                            </div>
                            <Opacity>
                                <div
                                    style="display: flex; justify-items: center; flex-direction: row;"
                                >
                                    <div>{$_('vehicle_available_from')}&nbsp;</div>
                                    <div>
                                        <Time
                                            seconds={alert.limit}
                                            displayFormat="time"
                                            reduceTo24h={true}
                                        />
                                    </div>
                                    <div>)</div>
                                </div>
                            </Opacity>
                        </div>
                    {:else if alert.type === 'work_time_end'}
                        <div
                            class="values"
                            style="display: flex; justify-items: center; flex-direction: row; column-gap: 3px"
                        >
                            <div class="value">
                                <Time
                                    seconds={valueToUse}
                                    displayFormat="time"
                                    reduceTo24h={true}
                                />
                            </div>
                            <Opacity>
                                <div
                                    style="display: flex; justify-items: center; flex-direction: row;"
                                >
                                    <div>{$_('vehicle_available_until')}&nbsp;</div>
                                    <div>
                                        <Time
                                            seconds={alert.limit}
                                            displayFormat="time"
                                            reduceTo24h={true}
                                        />
                                    </div>
                                    <div>)</div>
                                </div>
                            </Opacity>
                        </div>
                    {:else if alert.type === 'time_window'}
                        <span class="values">
                            <span class="value">
                                {#if valueToUse}
                                    {#if valueToUse >= 5}
                                        {valueToUse} {$_('Vehicle_limit_time_window_exceeded_short_many')}
                                    {:else}
                                        {valueToUse} {$_('Vehicle_limit_time_window_exceeded_short_few')}
                                    {/if}
                                {:else}
                                    {$_('Vehicle_limit_time_window_exceeded_all_fulfilled')}
                                {/if}
                            </span>
                        </span>
                    {:else}
                        {#if alert.count}
                            <span class="count" style="background-color: {alert.color}" title="{$_('Vehicle_limit_'+alert.type+'_count', {values: {count: alert.count}})}">{alert.count}</span>
                        {/if}
                        <span class="values">
                            <span class="value">{Math.ceil(valueToUse)}</span>/<span class="limit"
                                >{Math.ceil(alert.limit)}</span
                            >
                            {#if units && typeof types[alert.type]?.unit !== 'undefined'}
                                <span class="unit">{types[alert.type].unit}</span>
                            {/if}
                        </span>
                    {/if}
                {:else}
                    <span class="values">-</span>
                {/if}
            {/if}
            {#if percent && alert.limit && typeof types[alert.type]?.unit !== 'undefined'}
                <span class="percent" title={types[alert.type].title}>
                    {#if values}({/if}{Math.round(
                        (100 * (valueToUse || 0)) / alert.limit
                    )}%{#if values}){/if}
                </span>
            {/if}
            {#if alert.doubtReason && doubtReasons[alert.doubtReason]}
                <span class="annotation" title={$_('Vehicle_limit_doubt_annotation_'+alert.doubtReason)}>{doubtReasons[alert.doubtReason]}</span>
            {/if}
        </div>
    {/each}
    {#if hasNonNormativeOrders}
        <div class="alert" style="color: {VehicleLimitItem._VEHICLE_LIMIT_COLOR_WARNING}">
            {$_('Vehicle_limit_non_normative_jobs')}
        </div>
    {/if}
    {#if doubtReasons && Object.keys(doubtReasons).length > 0}
        <div class="annotations">
            {#each Object.entries(doubtReasons) as [key, value]}
                <div>{value}) {$_('Vehicle_limit_doubt_annotation_'+key)}</div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .container {
        font-size: 12px;
    }
    .title {
        font-size: 14px;
        white-space: nowrap;
        padding-bottom: 2px;
        margin-bottom: 5px;
        border-bottom: 1px solid #999;
    }
    .load {
        font-weight: bold;
    }
    .alert {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 3px;
        margin: 5px 0;
    }
    .values {
        font-size: 12px;
    }
    .value {
        display: inline-block;
        padding-right: 2px;
    }
    .limit {
        display: inline-block;
        padding-left: 2px;
    }
    .unit {
        opacity: 0.6;
    }
    .percent {
        font-weight: bold;
    }
    .count {
        display: inline-block;
        border-radius: 9px;
        text-align: center;
        color: #ffffff;
        height: 16px;
        min-width: 16px;
        line-height: 14px;
        font-size: 11px;
    }
    .annotations {
        opacity: 0.5;
        border-top: 1px solid #999;
        font-size: 80%;
    }
</style>
